import React from 'react';
import { useTranslation } from 'react-i18next';
import { XMarkIcon, CurrencyDollarIcon } from '@heroicons/react/24/outline';
import { IVideoLipsync, IUserModel } from '@packages/global';
import { useCoinStore } from '../../stores/coinStore';
import VideoUpload from '../common/VideoUpload';
import AudioUpload from '../common/AudioUpload';
import VideoTabs from '../common/VideoTabs';
import { ModelSelector } from '../common/form';

interface VideoLipsyncFormData {
  videoUrl: string;
  audioUrl: string;
  modelId: string;
}

interface VideoLipsyncFormProps {
  editingItem: IVideoLipsync | null;
  formData: VideoLipsyncFormData;
  models: IUserModel[];
  modelsLoading: boolean;
  onSubmit: (e: React.FormEvent) => void;
  onFormDataChange: (data: Partial<VideoLipsyncFormData>) => void;
  onReset: () => void;
}

export default function VideoLipsyncForm({
  editingItem,
  formData,
  models,
  modelsLoading,
  onSubmit,
  onFormDataChange,
  onReset
}: VideoLipsyncFormProps) {
  const { t } = useTranslation(['generation', 'common']);
  const { account } = useCoinStore();
  const selectedModel = models.find(m => m.id === formData.modelId);
  const modelPrice = selectedModel?.coinPrice ?? 0;
  const hasEnoughCoins = (account?.balance ?? 0) >= modelPrice;

  const isFormValid = (): boolean => {
    return !!(formData.videoUrl && formData.audioUrl && formData.modelId);
  };

  return (
    <div className="w-96 lg:w-[420px] bg-bg-form border-r border-border-subtle shadow-xl overflow-y-auto">
      <VideoTabs />

      <div className="p-6">
        <form onSubmit={onSubmit} className="space-y-6">
          {/* 标题行：重置按钮 */}
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-300">{t('generation:lipsync.form.title')}</span>
            {editingItem && (
              <button
                type="button"
                onClick={onReset}
                className="p-1.5 text-gray-400 hover:text-gray-200 hover:bg-bg-elevated rounded-lg transition-colors"
                title={t('generation:lipsync.form.resetForm')}
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* 模型选择 */}
          <ModelSelector
            models={models}
            selectedModelId={formData.modelId}
            onChange={(modelId) => onFormDataChange({ modelId })}
            loading={modelsLoading}
            placeholder={t('generation:lipsync.form.selectModel')}
          />

          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-50 border-b border-border-subtle pb-2">
              {t('generation:lipsync.form.contentConfig')}
            </h3>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                {t('generation:lipsync.form.originalVideo')}
              </label>
              <VideoUpload
                value={formData.videoUrl}
                onChange={(url) => onFormDataChange({ videoUrl: url })}
                placeholder={t('generation:lipsync.form.uploadVideoPlaceholder')}
              />
              <p className="mt-1 text-xs text-gray-400">
                {t('generation:lipsync.form.videoFormatHint')}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                {t('generation:lipsync.form.dubbingAudio')}
              </label>
              <AudioUpload
                value={formData.audioUrl}
                onUploadSuccess={(url) => onFormDataChange({ audioUrl: url })}
                placeholder={t('generation:lipsync.form.uploadAudioPlaceholder')}
                showAudioSelector={true}
                showMusicSelector={false}
              />
              <p className="text-xs text-gray-500 mt-1">
                {t('generation:lipsync.form.audioDurationHint')}
              </p>
            </div>
          </div>

          <div className="pt-4 space-y-2">
            <button
              type="submit"
              disabled={modelsLoading || !isFormValid() || !hasEnoughCoins}
              className="w-full bg-gradient-to-br from-primary to-primary-light hover:shadow-[0_0_20px_rgba(255,92,0,0.3)] disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium py-3 px-4 rounded-lg transition-all focus:outline-none focus:ring-2 focus:ring-primary/50"
            >
              {modelsLoading ? t('generation:lipsync.form.submitLoading') : t('generation:lipsync.form.submitButton')}
            </button>
            {!modelsLoading && modelPrice > 0 && (
              <div className="flex items-center justify-between text-xs px-1">
                <div className="flex items-center gap-1 text-gray-500">
                  <CurrencyDollarIcon className="w-3.5 h-3.5" />
                  <span>{t('generation:lipsync.form.cost')} <span className="font-mono text-primary">{modelPrice}</span></span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-gray-500">{t('generation:lipsync.form.balance')}</span>
                  <span className={`font-mono ${hasEnoughCoins ? 'text-gray-300' : 'text-error'}`}>
                    {account?.balance ?? 0}
                  </span>
                </div>
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}